/**
 * Canonical listing-type handling.
 *
 * Companion to util/userTypes.js, and it exists for the same reason: gates in
 * this codebase were written against listing-type ids that production does not
 * have, so they matched nothing and silently failed open.
 *
 * The AUTHORITATIVE list is the hosted Console asset
 * `/listings/listing-types.json`. For this marketplace it contains exactly two:
 *
 *   ilmoitus  "Ilmoitus"  a posting / advert   -- companies only
 *   request   "Request"   a service request    -- private people only
 *                         (label still English in Console; should be
 *                          "Tarjouspyyntö")
 *
 * `company-profile` is NOT in the hosted asset. It is the verified directory
 * entry and is created only by the server (create-company-profile.js) or by the
 * claim flow. It is defined locally in config/configListing.js purely so the
 * edit wizard can render an existing profile — it must never be selectable.
 *
 * Historical note: 'daily-booking' and 'service-request' were local-only ids
 * that leaked into production through the `shouldMerge = true` override in
 * util/configHelpers.js. The private-user restriction in
 * EditListingDetailsPanel filtered out 'daily-booking', a type that did not
 * exist, so private people were left holding 'ilmoitus' and could post adverts.
 * Removed 20.9.2026.
 */

/** A posting / advert. Only a verified company may create one. */
export const LISTING_TYPE_POSTING = 'ilmoitus';

/** A service request (tarjouspyyntö). Only a private person may create one. */
export const LISTING_TYPE_SERVICE_REQUEST = 'request';

/** The verified company directory entry. Never selectable in the wizard. */
export const LISTING_TYPE_COMPANY_PROFILE = 'company-profile';

/**
 * Operator-written marker proving Asiakastieto confirmed signing rights.
 *
 * Lives in `metadata`, which only the Integration API can write. `publicData`
 * is writable by the listing's owner and therefore proves nothing.
 */
export const SIGNING_RIGHTS_KEY = 'signingRights';

/** Listing types a verified company may create. */
export const COMPANY_LISTING_TYPES = [LISTING_TYPE_POSTING];

/** Listing types a private person may create. */
export const PRIVATE_LISTING_TYPES = [LISTING_TYPE_SERVICE_REQUEST];

/**
 * Is this listing a company profile that passed Asiakastieto?
 *
 * Accepts a listing entity from either the Marketplace or Integration API.
 */
export const isVerifiedCompanyProfile = listing => {
  const { publicData, metadata } = listing?.attributes || {};
  return (
    publicData?.listingType === LISTING_TYPE_COMPANY_PROFILE && !!metadata?.[SIGNING_RIGHTS_KEY]
  );
};
